import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

import { DroppedMainCrop, GenerateSoilSightOutcomesResponse } from "models";

import { type CropYear } from "pages/app/scenario-analysis/types/scenario";
import { createEmptyYear } from "pages/app/scenario-analysis/utils/cropYearHelpers";

const createDefaultCropYears = (): CropYear[] => [
  createEmptyYear("onboarding-y-1"),
  createEmptyYear("onboarding-y-2"),
  createEmptyYear("onboarding-y-3"),
  createEmptyYear("onboarding-y-4"),
];

export type OnboardingCalcStatus = "idle" | "calculating" | "error";

/** "skipped" = nothing to restore for this session (fresh flow, or the session created its own draft). */
export type OnboardingRestoreStatus = "idle" | "restoring" | "done" | "skipped" | "error";

export type RecommendationField = "residue" | "tillageId" | "coverCropId";

// Auto-recommended inputs keyed by crop-year id — the candidate set; step 3 derives what it renders.
export type RecommendationMap = Record<string, RecommendationField[]>;

export type OnboardingImportMeta = {
  importedFieldId: string;
  droppedMainCrops: DroppedMainCrop[];
  bannerDismissed: boolean;
  /** False when field management had no crop rotation to bring along (field-only import). */
  rotationImported: boolean;
};

export type OnboardingScenarioRefs = {
  projectId: string | null;
  baselineScenarioId: string | null;
  changeScenarioId: string | null;
};

export interface ScenarioOnboardingContextValue {
  /**
   * Shared serialization chain for everything that persists the baseline. The autosave and the
   * step-1 CTA both create the project if none exists, so running concurrently makes each create
   * its own (observed: two projects 3ms apart, the change scenario landing on only one of them).
   */
  draftSaveQueueRef: React.MutableRefObject<Promise<void>>;
  /** Project id as soon as it exists — `scenarioRefs` is state, invisible to a caller mid-tick. */
  persistedProjectIdRef: React.MutableRefObject<string | null>;
  selectedFarmId: string | null;
  selectedFieldId: string | null;
  cropYears: CropYear[];
  changeCropYears: CropYear[];
  scenarioRefs: OnboardingScenarioRefs;
  outcomes: GenerateSoilSightOutcomesResponse | null;
  calcStatus: OnboardingCalcStatus;
  importMeta: OnboardingImportMeta | null;
  validationAttempted: boolean;
  restoreStatus: OnboardingRestoreStatus;
  setSelectedFarmId: (id: string | null) => void;
  setSelectedFieldId: (id: string | null) => void;
  setCropYears: React.Dispatch<React.SetStateAction<CropYear[]>>;
  setChangeCropYears: React.Dispatch<React.SetStateAction<CropYear[]>>;
  setScenarioRefs: React.Dispatch<React.SetStateAction<OnboardingScenarioRefs>>;
  setOutcomes: (outcomes: GenerateSoilSightOutcomesResponse | null) => void;
  setCalcStatus: (status: OnboardingCalcStatus) => void;
  setImportMeta: React.Dispatch<React.SetStateAction<OnboardingImportMeta | null>>;
  setValidationAttempted: (attempted: boolean) => void;
  setRestoreStatus: (status: OnboardingRestoreStatus) => void;
  resetFlow: () => void;
}

const EMPTY_SCENARIO_REFS: OnboardingScenarioRefs = { projectId: null, baselineScenarioId: null, changeScenarioId: null };

const ScenarioOnboardingContext = createContext<ScenarioOnboardingContextValue | null>(null);

export const ScenarioOnboardingProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedFarmId, setSelectedFarmIdState] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [cropYears, setCropYears] = useState<CropYear[]>(createDefaultCropYears);
  const [changeCropYears, setChangeCropYears] = useState<CropYear[]>([]);
  const [scenarioRefs, setScenarioRefs] = useState<OnboardingScenarioRefs>(EMPTY_SCENARIO_REFS);
  const [outcomes, setOutcomes] = useState<GenerateSoilSightOutcomesResponse | null>(null);
  const [calcStatus, setCalcStatus] = useState<OnboardingCalcStatus>("idle");
  const [importMeta, setImportMeta] = useState<OnboardingImportMeta | null>(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [restoreStatus, setRestoreStatus] = useState<OnboardingRestoreStatus>("idle");

  const selectedFarmIdRef = useRef<string | null>(null);
  selectedFarmIdRef.current = selectedFarmId;

  const draftSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const persistedProjectIdRef = useRef<string | null>(null);

  const setSelectedFarmId = useCallback((id: string | null) => {
    const previous = selectedFarmIdRef.current;
    if (previous === id) return;
    // Everything derived from the previous farm's project is invalid; the rotation itself is kept so a
    // farm switch does not discard what the user already entered.
    if (previous !== null) {
      // Must be cleared with scenarioRefs: projects are farm-scoped, so keeping it would point the
      // next save at the previous farm's project.
      persistedProjectIdRef.current = null;
      setScenarioRefs(EMPTY_SCENARIO_REFS);
      setChangeCropYears([]);
      setOutcomes(null);
      setCalcStatus("idle");
      setImportMeta(null);
    }
    setSelectedFieldId(null);
    setSelectedFarmIdState(id);
  }, []);

  const resetFlow = useCallback(() => {
    persistedProjectIdRef.current = null;
    setSelectedFarmIdState(null);
    setSelectedFieldId(null);
    setCropYears(createDefaultCropYears());
    setChangeCropYears([]);
    setScenarioRefs(EMPTY_SCENARIO_REFS);
    setOutcomes(null);
    setCalcStatus("idle");
    setImportMeta(null);
    setValidationAttempted(false);
    setRestoreStatus("idle");
  }, []);

  const value = useMemo(
    () => ({
      draftSaveQueueRef,
      persistedProjectIdRef,
      selectedFarmId,
      selectedFieldId,
      cropYears,
      changeCropYears,
      scenarioRefs,
      outcomes,
      calcStatus,
      importMeta,
      validationAttempted,
      restoreStatus,
      setSelectedFarmId,
      setSelectedFieldId,
      setCropYears,
      setChangeCropYears,
      setScenarioRefs,
      setOutcomes,
      setCalcStatus,
      setImportMeta,
      setValidationAttempted,
      setRestoreStatus,
      resetFlow,
    }),
    [
      selectedFarmId,
      selectedFieldId,
      cropYears,
      changeCropYears,
      scenarioRefs,
      outcomes,
      calcStatus,
      importMeta,
      validationAttempted,
      restoreStatus,
      setSelectedFarmId,
      resetFlow,
    ]
  );

  return <ScenarioOnboardingContext.Provider value={value}>{children}</ScenarioOnboardingContext.Provider>;
};

export const useScenarioOnboarding = (): ScenarioOnboardingContextValue => {
  const ctx = useContext(ScenarioOnboardingContext);
  if (!ctx) throw new Error("useScenarioOnboarding must be used within ScenarioOnboardingProvider");
  return ctx;
};
